import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import useFollowedClosingSoon from "../../hooks/useFollowedClosingSoon";
import { pctTowardCap, viewsLeft } from "../../utils/followedClosingSoon";
import "./GameWithClosingNow.css";

/**
 * Home hides its right-hand panel at max-width 1150px.
 * This panel uses that same cutoff: it mounts only when the viewport is wider.
 */
const DESKTOP_PANEL_QUERY = "(min-width: 1151px)";
const COLLAPSED_KEY = "100vo.closingNow.collapsed";

function useDesktopPanel() {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(DESKTOP_PANEL_QUERY).matches
  );

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_PANEL_QUERY);
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return desktop;
}

function useClosingCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        /* Private browsing can block storage; the toggle still applies this session. */
      }
      return next;
    });
  };

  return [collapsed, toggle];
}

function dropThumb(drop) {
  return drop.thumbnailUrl || drop.thumbnail || drop.mediaUrl || drop.coverImage || drop.imageUrl || "";
}

function EdgeChevron({ points }) {
  const d = points === "left" ? "M10 3 L5 8 L10 13" : "M6 3 L11 8 L6 13";
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClosingNowPanel({ collapsed, onToggle }) {
  const { rows, ready } = useFollowedClosingSoon();

  if (collapsed) {
    return (
      <aside className="gclose-panel is-collapsed" aria-label="Closing now">
        <button type="button" className="gclose-tab" onClick={onToggle} aria-expanded="false">
          <EdgeChevron points="right" />
          <span>Closing now</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="gclose-panel" aria-label="Closing now">
      <div className="gclose-head">
        <h2 className="gclose-title">Closing now</h2>
        <div className="gclose-head-actions">
          <Link className="gclose-see" to="/gallery">See all</Link>
          <button
            type="button"
            className="gclose-collapse"
            onClick={onToggle}
            aria-expanded="true"
            aria-label="Collapse Closing now"
          >
            <EdgeChevron points="left" />
          </button>
        </div>
      </div>
      <p className="gclose-sub">From creators you follow, before the 100-view cap hits</p>
      {ready && rows.length === 0 && (
        <p className="gclose-empty">Nobody you follow is this close to the cap.</p>
      )}
      {rows.map(({ creator, drop }) => {
        const left = viewsLeft(drop);
        const label = left === 1 ? "1 view left" : `${left} views left`;
        const thumb = dropThumb(drop);
        return (
          <Link key={drop.id} className="gclose-card" to={`/drop/${drop.id}`}>
            {thumb ? (
              <img className="gclose-thumb" src={thumb} alt="" />
            ) : (
              <span className="gclose-thumb gclose-thumb-empty" aria-hidden="true" />
            )}
            <span className="gclose-card-body">
              <span className="gclose-card-title">{drop.title || "Untitled drop"}</span>
              <span className="gclose-meta">{creator.displayName || "Creator"} · {label}</span>
              <span className="gclose-bar" aria-hidden="true">
                <span className="gclose-bar-fill" style={{ width: `${pctTowardCap(drop)}%` }} />
              </span>
            </span>
          </Link>
        );
      })}
      <Link className="gclose-gallery" to="/gallery">Browse the gallery</Link>
    </aside>
  );
}

export default function GameWithClosingNow({ children }) {
  const desktop = useDesktopPanel();
  const [collapsed, toggleCollapsed] = useClosingCollapsed();

  return (
    <div className={`gclose${desktop ? " gclose-desktop" : ""}${desktop && collapsed ? " is-collapsed" : ""}`}>
      {desktop && <ClosingNowPanel collapsed={collapsed} onToggle={toggleCollapsed} />}
      <div className="gclose-game">{children}</div>
      {desktop && !collapsed && <div className="gclose-balance" aria-hidden="true" />}
    </div>
  );
}
